import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { Session } from '../../api/session';
import { getFeed, getSystemMessages, markSystemMessageRead } from '../../api/tenant';
import { moscowDayLabel, moscowTime, parseServerDate } from '../../domain/moscowTime';
import { Segmented } from '../../ui/Choice';
import { Skeleton } from '../../ui/Skeleton';
import p from '../passes/passes.module.css';
import s from './more.module.css';

/**
 * Notifications from managing companies (shown ones are marked as read) and, on the second tab, what happened to
 * the resident's passes — ordered, guest in, guest out, closed — from v1's activity log GET /v1/feed (#4216).
 */
export function NotificationsScreen({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'mc' | 'passes'>('mc');
  const messages = useQuery({ queryKey: ['system-messages'], queryFn: () => getSystemMessages(session) });
  const feed = useQuery({ queryKey: ['feed'], queryFn: () => getFeed(session), enabled: tab === 'passes' });
  const passEvents = (feed.data ?? []).filter((e) => e.subjectType === 'pass');

  useEffect(() => {
    const fresh = (messages.data ?? []).filter((m) => m.new);
    if (!fresh.length) return;
    void Promise.allSettled(fresh.map((m) => markSystemMessageRead(session, m.id))).then(() =>
      queryClient.invalidateQueries({ queryKey: ['new-count'] }),
    );
  }, [messages.data, session, queryClient]);

  const sorted = [...(messages.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const now = new Date();
  return (
    <main className={p.page}>
      <h1 className={p.title}>Уведомления</h1>
      <Segmented
        label="Уведомления"
        value={tab}
        items={[{ id: 'mc', label: 'От УК' }, { id: 'passes', label: 'Пропуска' }]}
        onChange={setTab}
      />
      {tab === 'passes' ? (
        !feed.data ? (
          <Skeleton height={120} />
        ) : passEvents.length === 0 ? (
          <p className={p.empty}>Событий по пропускам пока нет</p>
        ) : (
          <ul className={p.list} aria-label="События по пропускам">
            {passEvents.map((e, i) => {
              const body = (
                <span className={s.rowText}>
                  <span className={s.rowName}>{e.title}</span>
                  {e.message && <span className={s.text}>{e.message}</span>}
                  <span className={s.rowSub}>{e.happenedAt}</span>
                </span>
              );
              return (
                <li key={`${e.happenedAt}-${i}`}>
                  {e.subject?.id ? (
                    <Link to={`/passes/${e.subject.id}`} className={s.row} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {body}
                    </Link>
                  ) : (
                    <div className={s.row}>{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )
      ) : !messages.data ? (
        <Skeleton height={120} />
      ) : sorted.length === 0 ? (
        <p className={p.empty}>Уведомлений нет</p>
      ) : (
        <ul className={p.list}>
          {sorted.map((m) => {
            const at = parseServerDate(m.createdAt.replace('T', ' ').slice(0, 19));
            return (
              <li key={m.id} className={m.new ? `${s.row} ${s.new}` : s.row}>
                <span className={s.rowText}>
                  <span className={s.rowName}>{m.title}</span>
                  <span className={s.text}>{m.text}</span>
                  <span className={s.rowSub}>
                    {m.systemName}
                    {at && !Number.isNaN(at.getTime()) ? ` · ${moscowDayLabel(at, now)} ${moscowTime(at)}` : ''}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
